import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '../../services/authService';
import { apiJson, apiVoid } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import type {
  CreateResourceLinkRequest,
  ResourceLinkDto,
  ResourceLinkSavedResponse,
  UpdateResourceLinkRequest,
} from '@signalvote/shared';

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all volunteer resources
 */
export function useResources() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.resources.list(),
    queryFn: () =>
      apiJson<ResourceLinkDto[]>('/api/resourcelinks', { error: 'Failed to fetch resources' }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new resource
 */
export function useCreateResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateResourceLinkRequest) =>
      apiJson<ResourceLinkSavedResponse>('/api/resourcelinks', {
        method: 'POST',
        json: data,
        error: (body) => new Error(body.error || 'Failed to create resource'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.resources.all });
    },
  });
}

/**
 * Update an existing resource
 */
export function useUpdateResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateResourceLinkRequest }) =>
      apiJson<ResourceLinkSavedResponse>(`/api/resourcelinks/${id}`, {
        method: 'PUT',
        json: updates,
        error: (body) => new Error(body.error || 'Failed to update resource'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.resources.all });
    },
  });
}

/**
 * Delete a resource
 */
export function useDeleteResource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiVoid(`/api/resourcelinks/${id}`, {
        method: 'DELETE',
        error: (body) => new Error(body.error || 'Failed to delete resource'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.resources.all });
    },
  });
}
